import React, { Dispatch } from "react";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { includes, times } from "../../../utils/lodash";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import {
    shouldShowRowLeftLabel,
    isHeaderRow,
    shouldShowRowRightLabel,
    questionForCell,
    isSplitColumn,
    isRowMergeCells,
    getUseChoiceTexts,
    variableForCell,
    getColumnCount,
    openEndVariableName,
    hasRightLabels,
    hasLeftLabels,
    hasOpenEnd,
    cellHasError,
    runningTotalCellHasValidationError,
    mergeRunningTotalValidationResult,
} from "../../../models/utilities/gridUtilities";
import {
    getSettingString,
    getSettingBoolean,
    hasSetting,
    getSettingNumber,
} from "../../../models/utilities/settingUtilities";
import { getRendererProps, RendererProps } from "../BaseRenderer";
import GridCellRenderer from "./GridCellRenderer";
import OpenEndRenderer from "./OpenEndRenderer";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import { SignalListener } from "../../shared/SignalListener";
import { isNullOrEmpty } from "../../../utils";
import { RowType } from "../../../models/enums/RowType";
import { controlTypeForColumn } from "../../../models/factories/gridControlFactory";
import { VariableModel } from "../../../models/VariableModel";
import { AxisModel } from "../../../models/AxisModel";
import { translate } from "../../../models/utilities/translationUtilities";
import { ControlContainer } from "../../../containers/controls/ControlContainer";
import { Signal } from "../../../signals";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { ValidationOptionsModel } from "../../../models/ValidationOptionsModel";
import { ColumnType } from "../../../models/enums/ColumnType";
import { Collapsible } from "../../shared/Collapsible";
import useStyle from "./GridRenderer.styles";
import Button from "../../shared/Button";
import useQuestion, { QuestionContext } from "../../../hooks/useQuestion";
import {
    ChoiceGridFirstAriaLeadCellContext,
    type ChoiceGridFirstAriaLeadCellCoords,
    useChoiceGridFirstAriaLeadCellCoords,
} from "../../../hooks/ChoiceGridFirstAriaLeadCellContext";
import {
    ChoiceGridMatrixRadioGroupBridgeContext,
    useChoiceGridMatrixRadioGroupBridge,
    type ChoiceGridMatrixRadioGroupBridgeValue,
} from "../../../hooks/ChoiceGridMatrixRadioGroupBridgeContext";
import { RunningTotalFirstVariableCellContext } from "../../../hooks/RunningTotalFirstVariableCellContext";
import { GridCellValidationErrorContext } from "../../../hooks/GridCellValidationErrorContext";
import useTranslatable from "../../../hooks/useTranslatable";
import useRecall from "../../../hooks/useRecall";
import ChoiceListItemRenderer from "./ChoiceListItemRenderer";
import { defaultSize } from "../../../models/utilities/questionUtilities";
import InputWrapper from "../../shared/InputWrapper";
import { Translatable } from "../../../models/Translatable";
import { evaluateIfChoiceVisible } from "../../../redux/thunks/evaluator";
import { useMentions } from "../../../hooks/useMentions";
import { MentionModel } from "../../../models/MentionModel";
import { StoreState } from "../../../models/states/StoreState";
import { VariablesState } from "../../../models/states/VariablesState";
import { getMentions } from "../../../redux/reducers/interview/variablesUtilities";
import { setQuestionFieldStatus, setVariableMentions } from "../../../redux/actions";
import { QuestionModel } from "../../../models/QuestionModel";
import { SMALL_WIDTH } from "../../../consts";
import type { Theme } from "../../../types/Theme";
import {
    isModernGridTableQuestion,
    isModernGridDuoChromeStriping,
    isModernHybridGridTable,
    useModernGridTableStyles,
} from "../../../theme/gridThemeTokens";
import {
    getAriaLabelledByFromId,
    getQuestionTitleAndLabelAriaLabelledByIds,
    hybridGridLeadCellGroupAriaLabelledByIds,
    stripHtmlForAccessibleName,
} from "../../../utils/WCAGHelper";
import {
    HybridGridSubHeaderAriaContext,
    useHybridGridSubHeaderAria,
} from "./HybridGridSubHeaderAriaContext";
import {
    HybridGridFirstAriaLeadCellContext,
    type HybridGridFirstAriaLeadCellCoords,
    matchesHybridGridAriaLeadVariableCell,
    useHybridGridFirstAriaLeadCellCoords,
} from "../../../hooks/HybridGridFirstAriaLeadCellContext";
import { ModernGridRowStripeContext } from "../../../hooks/ModernGridRowStripeContext";
import {
    ModernSelectChevronDownIcon,
    ModernSelectChevronUpIcon,
} from "../../shared/ModernSelectIcons";

interface GridRendererProps extends RendererProps {
    rows: AxisModel[];
    columns: AxisModel[];
    validateSignal: Signal<ValidationOptionsModel, ValidationResultModel>;
    validationResult: ValidationResultModel;
    getSelectedAnswers?(row: AxisModel, column: AxisModel): ChoiceModel[];
    shouldHideCell?(row: AxisModel, column: AxisModel): boolean;
    shouldDisableCell?(row: AxisModel, column: AxisModel): boolean;
    validateCell?(row: AxisModel, column: AxisModel): ValidationResultModel;
    validateColumn?(column: AxisModel): ValidationResultModel;
    validateRowOpenEnd?(row: AxisModel): ValidationResultModel;
    validateChoiceOpenEnd?(column: AxisModel, choice: ChoiceModel): ValidationResultModel;
    getColumnRunningTotal?(column: AxisModel): string;
    isRowSelected?(row: AxisModel): boolean;
    expandRow?(row: AxisModel): void;
    addAccordionRef?(rowIndex: number, ref: HTMLDivElement): void;
    openEndValue?: { [key: string]: string };
    onOpenEndChange?(answer: any, event: any): void;
    onOpenEndBlur?(answer: any): void;
    showSoftPrompt?: boolean;
    matrix?: number;
    onReset?(): void;
    expandedRowsIndices?: number[];
    isScreenSmall?: boolean;
    isColumnChoiceSelected?(column: AxisModel, choice: ChoiceModel): boolean;
    max?: string;
    sequencedVariables?: VariableModel[];
    gridRenderedRowIndex?: number;
    gridRenderedColumnIndex?: number;
}

interface StyledProps extends GridRendererProps {
    classes: any;
}

interface ReferencedElementProps extends StyledProps {
    elementRef?: React.Ref<HTMLElement>;
}

interface RecalledLabelProps {
    translatable: Translatable;
    translatableKey: string;
    classes?: string;
    openEndAriaId?: string;
    cellId?: string;
}

const isTransposedGrid = (props: GridRendererProps) =>
    getSettingBoolean(props.question, "InverseRendering");

const shouldDisplayResetButton = (props: GridRendererProps) =>
    getSettingBoolean(props.question, "DisplayResetButton");

const defaultColumnsWidth = (props: GridRendererProps) =>
    columnsWidth(props, "DefaultColumnsWidth");

const firstColumnWidth = (props: GridRendererProps) => columnsWidth(props, "FirstColumnWidth");

const columnsWidth = (props: GridRendererProps, key: string) => {
    const settingValue = getSettingString(props.question, key);
    return includes(settingValue, "%") ? settingValue : null;
};

function RecalledLabel(props: RecalledLabelProps) {
    const { translatable, translatableKey, classes, openEndAriaId, cellId } = props;
    const label = useTranslatable(translatable, translatableKey);
    const recalled = useRecall(label);

    if (!recalled) {
        return null;
    }
    return (
        <div
            className={classes}
            {...(openEndAriaId ? { id: `${openEndAriaId}` } : {})}
            {...(cellId ? { id: `${cellId}` } : {})}
            dangerouslySetInnerHTML={{ __html: recalled }}
        />
    );
}

function LeftLabel(props: StyledProps & { axis: AxisModel }) {
    const { classes, axis, question } = props;
    const cellId = GetRowLeftLabelAriaId(axis, question);

    return (
        <RecalledLabel
            translatable={axis}
            translatableKey="LeftLabel"
            classes={classnames(classes.label, classes.labelLeft)}
            cellId={cellId}
        />
    );
}
function GetCellOpenEndId(rowOrChoice: AxisModel | ChoiceModel) {
    const leftLabel = useTranslatable(rowOrChoice, "LeftLabel");
    const text = useTranslatable(rowOrChoice, "TEXT");

    if (!hasSetting(rowOrChoice, "OpenEndType")) {
        return null;
    }

    if (leftLabel || text) {
        const openEndName = openEndVariableName(rowOrChoice);

        if (openEndName) {
            return `V_${openEndName}_CELL_LABEL`;
        }
    }
}

function GetRowLeftLabelAriaId(rowOrChoice: AxisModel | ChoiceModel, question: QuestionModel) {
    const leftLabel = useTranslatable(rowOrChoice, "LeftLabel");
    const text = useTranslatable(rowOrChoice, "TEXT");

    if (question.name && (leftLabel || text)) {
        return `Q_${question.name}_ROW_${rowOrChoice.index + 1}_LEFTLABEL`;
    }

    return null;
}

function RightLabel(props: StyledProps & { axis: AxisModel }) {
    const { classes, axis } = props;
    const openEndIdForCell = GetCellOpenEndId(axis);

    return (
        <RecalledLabel
            translatable={axis}
            translatableKey="RightLabel"
            classes={classnames(classes.label, classes.labelRight)}
            openEndAriaId={openEndIdForCell}
        />
    );
}

function Title(
    props: StyledProps & { row: AxisModel; column: AxisModel; showColumnHeaderId?: boolean },
) {
    const { classes, column, row, question, showColumnHeaderId } = props;
    const theme = useTheme() as Theme;
    let openEndIdForCell = GetCellOpenEndId(column);
    const needsColumnHeaderAriaId =
        question.type === QuestionType.RunningTotal ||
        question.type === QuestionType.HybridGrid ||
        question.type === QuestionType.StarRatingGrid;

    // Running total / hybrid / star-rating grids: stable HEADER ids for cell aria-labelledby (see StarRatingRenderer).
    if (needsColumnHeaderAriaId && !openEndIdForCell && showColumnHeaderId) {
        openEndIdForCell = `Q_${question.name}_HEADER_${column.index + 1}`;
    }

    const titleClass = classnames(
        classes.title,
        question.type === QuestionType.RunningTotal &&
            theme.UseModernTheme === true &&
            classes.titleRunningTotal,
        isModernGridTableQuestion(question.type) &&
            useModernGridTableStyles(theme) &&
            classes.titleGridModern,
    );

    return (
        <RecalledLabel
            translatable={row}
            translatableKey={`Labels_${column.index + 1}`}
            classes={titleClass}
            openEndAriaId={openEndIdForCell}
        />
    );
}

function Text(props: StyledProps & { row: AxisModel; column: AxisModel; index: number }) {
    const { classes, column, row, index, question } = props;
    const theme = useTheme() as Theme;
    const openEndIdForCell = GetCellOpenEndId(column);
    const cellVariable = column.variables.find(
        (variableItem) => variableItem.choiceList.length > 0,
    );
    const shouldSplitSubHeaders =
        question.type === QuestionType.HybridGrid &&
        row.type === RowType.Text &&
        isSplitColumn(column) &&
        Boolean(cellVariable);

    let hybridSubHeaderId: string | undefined;
    if (question.type === QuestionType.HybridGrid && row.type === RowType.Text) {
        const base = `Q_${question.name}_SUBHEADER_${column.index + 1}`;
        hybridSubHeaderId = shouldSplitSubHeaders ? `${base}_${index + 1}` : base;
    }

    const openEndAriaId = openEndIdForCell || hybridSubHeaderId;
    const useHybridSubHeaderColor =
        question.type === QuestionType.HybridGrid &&
        row.type === RowType.Text &&
        isModernHybridGridTable(question.type, theme);

    return (
        <RecalledLabel
            translatable={row}
            translatableKey={`Labels_${column.index + 1}_${index + 1}`}
            openEndAriaId={openEndAriaId}
            classes={useHybridSubHeaderColor ? classes.gridSubHeaderText : undefined}
        />
    );
}

function ChoiceText(props: StyledProps & { choice: ChoiceModel; row?: AxisModel }) {
    const { choice, row, question, classes } = props;
    const theme = useTheme() as Theme;
    const openEndIdForCell = GetCellOpenEndId(choice);
    const useHybridSubHeaderColor =
        question.type === QuestionType.HybridGrid &&
        row?.type === RowType.Text &&
        isModernHybridGridTable(question.type, theme);

    return (
        <RecalledLabel
            translatable={choice}
            translatableKey="TEXT"
            openEndAriaId={openEndIdForCell}
            classes={useHybridSubHeaderColor ? classes.gridSubHeaderText : undefined}
        />
    );
}

function RowOpenEnd(props: GridRendererProps & { row: AxisModel }) {
    const { row } = props;
    const isHidden = row.openEndActive !== undefined ? !row.openEndActive : false;
    const isDisabledIfHidden = getSettingBoolean(row, "DisplayIfDisableInstead");
    const isDisabled = row.openEndDisplayIf && isHidden && isDisabledIfHidden;

    return (
        <OpenEnd
            rowOrChoice={row}
            isHidden={isHidden}
            isDisabledIfHidden={isDisabledIfHidden}
            isDisabled={isDisabled}
            {...props}
            isRowHeader={true}
        />
    );
}

function ChoiceColumnOpenEnd(
    props: GridRendererProps & { choice: ChoiceModel; column: AxisModel },
) {
    const { choice } = props;
    const isHidden = choice.openEndActive !== undefined ? !choice.openEndActive : false;
    const isDisabledIfHidden = getSettingBoolean(choice, "DisplayIfDisableInstead");
    const isDisabled = choice.openEndDisplayLogic && isHidden && isDisabledIfHidden;

    return (
        <OpenEnd
            rowOrChoice={choice}
            isHidden={isHidden}
            isDisabledIfHidden={isDisabledIfHidden}
            isDisabled={isDisabled}
            {...props}
            isRowHeader={false}
        />
    );
}

function OpenEnd(
    props: GridRendererProps & {
        rowOrChoice: AxisModel | ChoiceModel;
        isHidden: boolean;
        isDisabledIfHidden: boolean;
        isDisabled: boolean;
    },
) {
    const { rowOrChoice, isHidden, isDisabledIfHidden, isDisabled, question } = props;
    const leftLabel = useTranslatable(rowOrChoice, "LeftLabel");
    const text = useTranslatable(rowOrChoice, "TEXT");

    if (!hasSetting(rowOrChoice, "OpenEndType")) {
        return null;
    }

    const openEndName = openEndVariableName(rowOrChoice);
    const { validationResult, openEndValue, onOpenEndBlur, onOpenEndChange } = props;

    const size = getSettingString(rowOrChoice, "OpenEndSize", "Medium");
    const lines = getSettingNumber(rowOrChoice, "OpenEndLines", 2);
    // Row answer variable used by grid cells — Tab walks OEs by this id (ChoiceGridRendererNavigation).
    // Hybrid rows have one variableId per column; anchoring to a middle choiceList column
    // inserts the OE between cells. Always use the first column variable for Hybrid.
    const rowVariables = (rowOrChoice as AxisModel).variables;
    const isHybridRow =
        question?.type === QuestionType.HybridGrid && Array.isArray(rowVariables);
    const focusVariableId = rowVariables?.length
        ? (isHybridRow
              ? rowVariables[0]
              : rowVariables.find((v) => v.choiceList?.length > 0) ?? rowVariables[0]
          )?.id
        : undefined;
    const focusRowIndex = isHybridRow
        ? ((rowOrChoice as AxisModel).index ?? props.gridRenderedRowIndex)
        : undefined;
    return (
        <OpenEndRenderer
            {...getRendererProps(props)}
            name={`V_${openEndName}`}
            label={leftLabel || text}
            isHidden={isHidden}
            value={openEndValue[openEndName]}
            isDisabledRequest={isDisabledIfHidden}
            isDisabled={isDisabled}
            onOpenEndBlur={() => onOpenEndBlur(rowOrChoice)}
            onOpenEndChange={(e) => onOpenEndChange(rowOrChoice, e)}
            validationResult={validationResult}
            size={size}
            lines={lines}
            gridRendering
            focusVariableId={focusVariableId}
            focusRowIndex={focusRowIndex}
        />
    );
}

function RowLeftLabel(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    if (!shouldShowRowLeftLabel(row)) {
        return null;
    }
    const { validationResult, validateSignal } = props;
    const isColumnHeader = isHeaderRow(row);
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            isColumnHeader={isColumnHeader}
            isRowHeader={!isColumnHeader}
            validationResult={validationResult}
            row={row}
            gridRenderedRowIndex={row.index}
        >
            <LeftLabel {...props} axis={row} />
            <RowOpenEnd row={row} {...props} gridRenderedRowIndex={row.index} />
            {hasSetting(row, "OpenEndType") && (
                <SignalListener
                    signal={validateSignal}
                    handler={() => props.validateRowOpenEnd(row)}
                />
            )}
        </GridCellRenderer>
    );
}

function RowRightLabel(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    if (!shouldShowRowRightLabel(row)) {
        return null;
    }
    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            isColumnHeader={isHeaderRow(row)}
            isRowHeader={!isHeaderRow(row)}
            validationResult={validationResult}
            row={row}
        >
            <RightLabel {...props} axis={row} />
        </GridCellRenderer>
    );
}

function GetQuestionFieldStatus(fieldIdentifier: string) {
    return useSelector((state: StoreState) => state.interview.questionFieldStatus[fieldIdentifier]);
}

function Choice(
    props: StyledProps & {
        row: AxisModel;
        column: AxisModel;
        variable: VariableModel;
        choice: ChoiceModel;
        isCollapsible?: boolean;
        label?: string;
    },
) {
    const {
        question,
        questionnaire,
        row,
        column,
        variable,
        choice,
        isCollapsible,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
    } = props;
    const theme = useTheme() as Theme;
    const isModernCollapsible =
        isCollapsible &&
        isModernGridTableQuestion(question.type) &&
        useModernGridTableStyles(theme);
    const [mentions] = useMentions(variable.name);
    const variables = useSelector((state: StoreState) => state.interview.variables);
    const dispatch = useDispatch();
    const isSelected = isChoiceSelected(choice, mentions);
    const cellQuestion = questionForCell(question, row, column, questionnaire);
    const fieldIdentifier = `${question.id}&${cellQuestion.name}`;
    const isHybridGrid = question?.type === QuestionType.HybridGrid;
    let isDisabled: boolean;
    let stateDisabledStatus;
    if (isHybridGrid) {
        stateDisabledStatus = GetQuestionFieldStatus(fieldIdentifier)?.isDisabled;

        if (stateDisabledStatus) {
            isDisabled = stateDisabledStatus;
        } else {
            isDisabled = isChoiceDisabled(props, mentions, variables, dispatch);
        }
    } else {
        isDisabled = isChoiceDisabled(props, mentions, variables, dispatch);
    }

    React.useEffect(() => {
        if (isHybridGrid && stateDisabledStatus === undefined) {
            dispatch(setQuestionFieldStatus([fieldIdentifier], isDisabled, false));
        } /* eslint-disable react-hooks/exhaustive-deps */
    }, []); // Runs once per component(Grid Cell)
    /* eslint-enable react-hooks/exhaustive-deps */

    const choiceRef = React.useRef();

    const isRadioOrCheckbox = [ColumnType.RadioButton, ColumnType.CheckBox].includes(
        column.type as ColumnType,
    );

    const applyCollapsibleStyles = isCollapsible && isRadioOrCheckbox;
    if (!applyCollapsibleStyles) {
        return (
            <div key={choice.index}>
                <ChoiceContainer
                    question={questionForCell(question, row, column, questionnaire)}
                    gridRendering={!isCollapsible}
                    choice={choice}
                    choiceRef={choiceRef}
                    controlType={controlTypeForColumn(column)}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    variable={variable}
                    executeUpdateForIdAfterSelection={fieldIdentifier}
                    gridRenderedRowIndex={row.index}
                    gridRenderedColumnIndex={column.index}
                />
            </div>
        );
    }

    return (
        <ChoiceListItemRenderer
            {...getRendererProps(props)}
            controlType={controlTypeForColumn(column)}
            gridRendering={!isCollapsible}
            openEnd={(openEndValue || {})[choice.index]}
            onOpenEndChange={onOpenEndChange}
            onOpenEndBlur={onOpenEndBlur}
            selected={isSelected}
            choice={choice}
            variable={variable}
            disabled={isDisabled}
            flatModernSurface={isModernCollapsible}
        />
    );
}

function ChoiceCells(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const { row, column, validateSignal, question, classes, language } = props;
    const includeHybridGridSubHeaderChrome = useHybridGridSubHeaderAria();
    const hybridLeadCoords = useHybridGridFirstAriaLeadCellCoords();
    const choiceGridLeadCoords = useChoiceGridFirstAriaLeadCellCoords();
    const choiceGridMatrixRadioBridge = useChoiceGridMatrixRadioGroupBridge();
    const variable = variableForCell(row, column);
    const colSpan = question.variables?.find((v) => v.name === variable.name).choiceList?.length;

    const isHybridLeadVariableCell =
        question.type === QuestionType.HybridGrid &&
        matchesHybridGridAriaLeadVariableCell(
            hybridLeadCoords,
            row.index,
            column.index,
            variable.id,
        ) === true;

    const cellChromeInputBoxIds =
        question.type === QuestionType.HybridGrid && Boolean(question.elementId)
            ? getAriaLabelledByFromId(
                  question,
                  question.elementId,
                  `${QuestionType.HybridGrid}_INPUTBOX`,
                  column.index + 1,
                  row.index + 1,
                  undefined,
                  language,
                  undefined,
                  includeHybridGridSubHeaderChrome,
              )
            : "";

    const hybridBridgeLabelledBy = (isLead: boolean) =>
        hybridGridLeadCellGroupAriaLabelledByIds(question, language, isLead, cellChromeInputBoxIds);

    const getHybridGroupOwnsIds = (choiceList: ChoiceModel[]) =>
        choiceList
            .filter((c) => c.elementId)
            .map((c) => `${c.elementId}_INPUT`)
            .join(" ");

    const useHybridGroupBridge =
        question.type === QuestionType.HybridGrid &&
        variable.choiceList.length >= 1 &&
        Boolean(question.elementId);

    const useHybridRadioGroupBridge =
        useHybridGroupBridge && column.type === ColumnType.RadioButton;

    const useHybridCheckboxGroupBridge =
        useHybridGroupBridge && column.type === ColumnType.CheckBox;

    const useHybridImageGroupBridge =
        useHybridGroupBridge && column.type === ColumnType.ImageSelector;

    const useHybridMultiInputGroupBridge =
        useHybridCheckboxGroupBridge || useHybridImageGroupBridge;

    const hybridRadioGroupLabelIds = useHybridRadioGroupBridge
        ? hybridBridgeLabelledBy(isHybridLeadVariableCell)
        : undefined;

    const hybridMultiInputGroupLabelIds = useHybridMultiInputGroupBridge
        ? hybridBridgeLabelledBy(isHybridLeadVariableCell)
        : undefined;

    const hybridRadioGroupOwnsIds = useHybridRadioGroupBridge
        ? getHybridGroupOwnsIds(variable.choiceList)
        : undefined;

    const hybridMultiInputGroupOwnsIds = useHybridMultiInputGroupBridge
        ? getHybridGroupOwnsIds(variable.choiceList)
        : undefined;

    return (
        <>
            <SignalListener
                key="signal"
                handler={() => props.validateCell(row, column)}
                signal={validateSignal}
            />
            {variable.choiceList.length > 0 ? (
                variable.choiceList.map((choice, choiceIndex) => (
                    <GridCellRenderer
                        {...getRendererProps(props)}
                        key={choice.elementId}
                        validationResult={props.validationResult}
                        row={row}
                        column={column}
                        choice={choice}
                    >
                        {choiceIndex === 0 &&
                        choiceGridMatrixRadioBridge != null &&
                        column.type === ColumnType.RadioButton &&
                        matchesChoiceGridAriaLeadChoiceCell(
                            row,
                            column,
                            variable,
                            choice,
                            choiceGridLeadCoords,
                        ) ? (
                            <div
                                role="radiogroup"
                                aria-labelledby={choiceGridMatrixRadioBridge.questionLabelIds}
                                aria-owns={choiceGridMatrixRadioBridge.ownsIds}
                                tabIndex={-1}
                                className={classes.hybridRadioGroupBridge}
                            />
                        ) : choiceIndex === 0 && useHybridRadioGroupBridge ? (
                            <div
                                role="radiogroup"
                                aria-labelledby={hybridRadioGroupLabelIds || undefined}
                                aria-owns={hybridRadioGroupOwnsIds || undefined}
                                tabIndex={-1}
                                className={classes.hybridRadioGroupBridge}
                            />
                        ) : choiceIndex === 0 && useHybridMultiInputGroupBridge ? (
                            <div
                                role="group"
                                aria-labelledby={hybridMultiInputGroupLabelIds || undefined}
                                aria-owns={hybridMultiInputGroupOwnsIds || undefined}
                                tabIndex={-1}
                                className={classes.hybridRadioGroupBridge}
                            />
                        ) : null}
                        {(() => {
                            const choiceGridGroupLabelIds =
                                choiceGridMatrixRadioBridge != null
                                    ? undefined
                                    : getChoiceGridQuestionGroupLabelIdsForLeadCell(
                                          question,
                                          row,
                                          column,
                                          variable,
                                          choice,
                                          language,
                                          choiceGridLeadCoords,
                                      );
                            const choiceControl = (
                                <Choice
                                    row={row}
                                    column={column}
                                    variable={variable}
                                    choice={choice}
                                    {...props}
                                />
                            );
                            return choiceGridGroupLabelIds ? (
                                <div role="group" aria-labelledby={choiceGridGroupLabelIds}>
                                    {choiceControl}
                                </div>
                            ) : (
                                choiceControl
                            );
                        })()}
                    </GridCellRenderer>
                ))
            ) : (
                <td colSpan={colSpan} />
            )}
        </>
    );
}

function VariableCell(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const {
        shouldDisableCell,
        validationResult,
        question,
        questionnaire,
        showSoftPrompt,
        validateSignal,
        row,
        column,
        language,
    } = props;

    const dispatch = useDispatch();
    const hybridLeadCoords = useHybridGridFirstAriaLeadCellCoords();
    const cellQuestion = questionForCell(question, row, column, questionnaire);
    const fieldIdentifier = `${question.id}&${cellQuestion.name}`;
    const [gridVariable] = cellQuestion.variables;

    let isDisabled = false;
    let stateDisabledStatus;
    const isHybridGrid = question.type === QuestionType.HybridGrid;

    if (isHybridGrid) {
        stateDisabledStatus = GetQuestionFieldStatus(fieldIdentifier);

        if (stateDisabledStatus) {
            isDisabled = stateDisabledStatus.isDisabled;
        } else {
            isDisabled = shouldDisableCell && shouldDisableCell(row, column);
        }
    } else {
        isDisabled = shouldDisableCell && shouldDisableCell(row, column);
    }

    React.useEffect(() => {
        if (isHybridGrid && stateDisabledStatus === undefined) {
            dispatch(setQuestionFieldStatus([fieldIdentifier], isDisabled, false));
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []); // Runs once per component(Grid Cell)
    /* eslint-enable react-hooks/exhaustive-deps */

    const isHybridLeadVariableCell =
        isHybridGrid &&
        gridVariable != null &&
        matchesHybridGridAriaLeadVariableCell(
            hybridLeadCoords,
            row.index,
            column.index,
            gridVariable.id,
        ) === true;

    const hybridLeadTitleIds =
        isHybridLeadVariableCell && language
            ? getQuestionTitleAndLabelAriaLabelledByIds(question, language)
            : undefined;

    const reduxErrorsByVariableName = useSelector(
        (state: StoreState) => state.interview.validation?.errorsByVariableName,
    );
    const cellMentionValue = useSelector((state: StoreState) => {
        if (!gridVariable?.name) {
            return "";
        }
        const mentions = state.interview.variables.mentionsByVariableName[gridVariable.name];
        return mentions?.[0]?.value ?? "";
    });
    const hasCellValidationError =
        question.type === QuestionType.RunningTotal
            ? runningTotalCellHasValidationError(
                  row,
                  column,
                  question,
                  cellMentionValue,
                  validationResult,
                  reduxErrorsByVariableName,
              )
            : cellHasError(row, column, validationResult) &&
              isNullOrEmpty(String(cellMentionValue).trim());

    const cellControl = (
        <QuestionContext.Provider value={cellQuestion}>
            <ControlContainer
                key={column.index}
                question={cellQuestion}
                controlType={controlTypeForColumn(column)}
                gridRendering
                showSoftPrompt={showSoftPrompt}
                validateSignal={validateSignal}
                isDisabled={isDisabled}
                executeUpdateForIdAfterSelection={fieldIdentifier}
                gridRenderedColumnIndex={column.index}
                gridRenderedRowIndex={row.index}
            />
        </QuestionContext.Provider>
    );

    const inner =
        question.type === QuestionType.RunningTotal ? (
            <GridCellValidationErrorContext.Provider value={hasCellValidationError}>
                {cellControl}
            </GridCellValidationErrorContext.Provider>
        ) : (
            cellControl
        );

    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {hybridLeadTitleIds ? (
                <div role="group" aria-labelledby={hybridLeadTitleIds}>
                    {inner}
                </div>
            ) : (
                inner
            )}
        </GridCellRenderer>
    );
}

function findFirstRunningTotalVariableCell(
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell?: (row: AxisModel, column: AxisModel) => boolean,
): { rowIndex: number; colIndex: number } | null {
    for (const r of rows) {
        if (r.type !== RowType.Variable) {
            continue;
        }
        for (const col of columns) {
            if (shouldHideCell && shouldHideCell(r, col)) {
                continue;
            }
            return { rowIndex: r.index, colIndex: col.index };
        }
    }
    return null;
}

function findFirstChoiceGridAriaLeadCell(
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell?: (row: AxisModel, column: AxisModel) => boolean,
): ChoiceGridFirstAriaLeadCellCoords | null {
    for (const row of rows) {
        if (row.type !== RowType.Variable) {
            continue;
        }
        for (const column of columns) {
            if (shouldHideCell && shouldHideCell(row, column)) {
                continue;
            }
            if (!isSplitColumn(column)) {
                continue;
            }
            const variable = variableForCell(row, column);
            const firstChoice = variable.choiceList[0];
            if (!firstChoice) {
                continue;
            }
            return {
                rowIndex: row.index,
                colIndex: column.index,
                choiceElementId: firstChoice.elementId,
                variableId: variable.id,
            };
        }
    }
    return null;
}

function findFirstChoiceGridAriaLeadCellTransposed(
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell?: (row: AxisModel, column: AxisModel) => boolean,
): ChoiceGridFirstAriaLeadCellCoords | null {
    for (const column of columns) {
        const choiceList = column.variables.find((v) => v.choiceList?.length > 0)?.choiceList;
        if (!choiceList?.length) {
            continue;
        }
        for (const choice of choiceList) {
            for (const row of rows) {
                if (row.type !== RowType.Variable) {
                    continue;
                }
                if (!isSplitColumn(column)) {
                    continue;
                }
                if (shouldHideCell && shouldHideCell(row, column)) {
                    continue;
                }
                const variable = variableForCell(row, column);
                if (!variable.choiceList.some((c) => c.elementId === choice.elementId)) {
                    continue;
                }
                return {
                    rowIndex: row.index,
                    colIndex: column.index,
                    choiceElementId: choice.elementId,
                    variableId: variable.id,
                };
            }
        }
    }
    return null;
}

function getChoiceGridAriaLeadCellCoords(props: GridRendererProps): ChoiceGridFirstAriaLeadCellCoords | null {
    const { question, rows, columns, shouldHideCell } = props;
    if (question.type !== QuestionType.ChoiceGrid && question.type !== QuestionType.ImageChoiceGrid) {
        return null;
    }
    return isTransposedGrid(props)
        ? findFirstChoiceGridAriaLeadCellTransposed(rows, columns, shouldHideCell)
        : findFirstChoiceGridAriaLeadCell(rows, columns, shouldHideCell);
}

function matchesChoiceGridAriaLeadChoiceCell(
    row: AxisModel,
    column: AxisModel,
    variable: VariableModel,
    choice: ChoiceModel,
    leadCoords: ChoiceGridFirstAriaLeadCellCoords | null | undefined,
): boolean {
    if (leadCoords === undefined || leadCoords === null) {
        return false;
    }
    return (
        row.index === leadCoords.rowIndex &&
        column.index === leadCoords.colIndex &&
        choice.elementId === leadCoords.choiceElementId &&
        variable.id === leadCoords.variableId
    );
}

/** Space-separated `*_INPUT` ids for every visible ChoiceGrid matrix radio (custom role="radio"). */
function collectChoiceGridMatrixRadioOwnsIds(
    question: QuestionModel,
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell: GridRendererProps["shouldHideCell"],
    transposed: boolean,
): string | undefined {
    if (question.type !== QuestionType.ChoiceGrid) {
        return undefined;
    }
    const ids: string[] = [];
    if (transposed) {
        for (const column of columns) {
            if (!isSplitColumn(column) || column.type !== ColumnType.RadioButton) {
                continue;
            }
            const choiceList = column.variables.find((v) => v.choiceList?.length > 0)?.choiceList;
            if (!choiceList?.length) {
                continue;
            }
            for (const choice of choiceList) {
                if (!choice.elementId) {
                    continue;
                }
                for (const row of rows) {
                    if (row.type !== RowType.Variable) {
                        continue;
                    }
                    if (shouldHideCell?.(row, column)) {
                        continue;
                    }
                    const variable = variableForCell(row, column);
                    if (!variable.choiceList.some((c) => c.elementId === choice.elementId)) {
                        continue;
                    }
                    ids.push(`${choice.elementId}_INPUT`);
                }
            }
        }
    } else {
        for (const row of rows) {
            if (row.type !== RowType.Variable) {
                continue;
            }
            for (const column of columns) {
                if (shouldHideCell?.(row, column)) {
                    continue;
                }
                if (!isSplitColumn(column) || column.type !== ColumnType.RadioButton) {
                    continue;
                }
                const variable = variableForCell(row, column);
                for (const choice of variable.choiceList) {
                    if (choice.elementId) {
                        ids.push(`${choice.elementId}_INPUT`);
                    }
                }
            }
        }
    }
    return ids.length > 0 ? ids.join(" ") : undefined;
}

function getChoiceGridMatrixRadioGroupBridgeValue(
    props: GridRendererProps,
): ChoiceGridMatrixRadioGroupBridgeValue | null {
    const { question, language, rows, columns, shouldHideCell } = props;
    if (question.type !== QuestionType.ChoiceGrid || !language) {
        return null;
    }
    const ownsIds = collectChoiceGridMatrixRadioOwnsIds(
        question,
        rows,
        columns,
        shouldHideCell,
        isTransposedGrid(props),
    );
    if (!ownsIds) {
        return null;
    }
    const titleIds = getQuestionTitleAndLabelAriaLabelledByIds(question, language);
    return {
        ownsIds,
        questionLabelIds: titleIds.length > 0 ? titleIds : undefined,
    };
}

/**
 * Question TITLE/LABEL ids for the first visible lead choice cell only — applied on a wrapping
 * role="group" so the interactive control keeps row/column ids only (avoids SR splitting one long aria-labelledby).
 */
function getChoiceGridQuestionGroupLabelIdsForLeadCell(
    question: QuestionModel,
    row: AxisModel,
    column: AxisModel,
    variable: VariableModel,
    choice: ChoiceModel,
    language: string | undefined,
    leadCoords: ChoiceGridFirstAriaLeadCellCoords | null | undefined,
): string | undefined {
    if (
        question.type !== QuestionType.ChoiceGrid &&
        question.type !== QuestionType.ImageChoiceGrid
    ) {
        return undefined;
    }
    if (!language || leadCoords === undefined || leadCoords === null) {
        return undefined;
    }
    if (!matchesChoiceGridAriaLeadChoiceCell(row, column, variable, choice, leadCoords)) {
        return undefined;
    }
    const ids = getQuestionTitleAndLabelAriaLabelledByIds(question, language);
    return ids.length > 0 ? ids : undefined;
}

function findFirstHybridGridAriaLeadCell(
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell?: (row: AxisModel, column: AxisModel) => boolean,
): HybridGridFirstAriaLeadCellCoords | null {
    for (const row of rows) {
        if (row.type !== RowType.Variable) {
            continue;
        }
        for (const column of columns) {
            if (shouldHideCell && shouldHideCell(row, column)) {
                continue;
            }
            const variable = variableForCell(row, column);
            if (isSplitColumn(column)) {
                const firstChoice = variable.choiceList[0];
                if (!firstChoice) {
                    continue;
                }
                return {
                    rowIndex: row.index,
                    colIndex: column.index,
                    variableId: variable.id,
                    choiceElementId: firstChoice.elementId,
                };
            }
            return {
                rowIndex: row.index,
                colIndex: column.index,
                variableId: variable.id,
            };
        }
    }
    return null;
}

function findFirstHybridGridAriaLeadCellTransposed(
    rows: AxisModel[],
    columns: AxisModel[],
    shouldHideCell?: (row: AxisModel, column: AxisModel) => boolean,
): HybridGridFirstAriaLeadCellCoords | null {
    for (const column of columns) {
        for (const row of rows) {
            if (row.type !== RowType.Variable) {
                continue;
            }
            if (shouldHideCell && shouldHideCell(row, column)) {
                continue;
            }
            const variable = variableForCell(row, column);
            if (isSplitColumn(column)) {
                const firstChoice = variable.choiceList[0];
                if (!firstChoice) {
                    continue;
                }
                return {
                    rowIndex: row.index,
                    colIndex: column.index,
                    variableId: variable.id,
                    choiceElementId: firstChoice.elementId,
                };
            }
            return {
                rowIndex: row.index,
                colIndex: column.index,
                variableId: variable.id,
            };
        }
    }
    return null;
}

function renderVariableCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { shouldHideCell, question } = props;
    const shouldSplitColumn = isSplitColumn(column);
    if (shouldHideCell && shouldHideCell(row, column)) {
        if (shouldSplitColumn) {
            const variable = variableForCell(row, column);

            if (variable.choiceList.length > 0) {
                return variable.choiceList.map((choice) => (
                    <GridCellRenderer
                        {...getRendererProps(props)}
                        key={choice.index}
                        validationResult={props.validationResult}
                        row={row}
                        column={column}
                        choice={choice}
                    />
                ));
            }
            const questionVariable = question.variables?.find((v) => v.name === variable.name);
            const colSpan = questionVariable.choiceList?.length;
            return <td colSpan={colSpan} />;
        }

        return (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={column.index}
                validationResult={props.validationResult}
                row={row}
                column={column}
            />
        );
    }
    if (shouldSplitColumn) {
        const choiceCellsKey = `${row.index}_${column.index}`;
        return <ChoiceCells key={choiceCellsKey} {...props} row={row} column={column} />;
    }
    return <VariableCell {...props} row={row} column={column} />;
}

function CellImage(props: StyledProps & { choice: ChoiceModel }) {
    const { choice } = props;
    const imageUrl = useTranslatable(choice, "ImageUrl");
    const validTypes = [QuestionType.ChoiceGrid, QuestionType.NetPromoter];

    if (validTypes.some((t) => t === props.question.type)) {
        if (!isNullOrEmpty(imageUrl)) {
            return <img src={imageUrl} alt={choice.name} />;
        }
    }

    return null;
}

function CellItems(props: StyledProps & { column: AxisModel; choice: ChoiceModel }) {
    const { question, column, choice } = props;
    const isNetPromoter = question.type === QuestionType.NetPromoter;
    const items = (
        <>
            <ChoiceColumnOpenEnd
                choice={choice}
                column={column}
                {...props}
                gridRenderedColumnIndex={column.index}
            />
            <CellImage {...props} choice={choice} />
        </>
    );
    return isNetPromoter ? <p>{items}</p> : items;
}

function renderChoiceTextCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { question } = props;

    if (isSplitColumn(column)) {
        const choiceList = column.variables.find((v) => v.choiceList?.length > 0)?.choiceList;

        if (!choiceList) {
            const questionVariable = question.variables?.find((v) => v.choiceList?.length > 0);
            const colSpan = questionVariable?.choiceList?.length;

            return <th colSpan={colSpan} key={column.index} />;
        }
        return choiceList?.map((choice) => (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={choice.index}
                isChoiceHeader
                validationResult={props.validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <ChoiceText {...props} choice={choice} row={row} />
                <CellItems {...props} column={column} choice={choice} />
                <SignalListener
                    signal={props.validateSignal}
                    handler={() => props.validateChoiceOpenEnd(column, choice)}
                />
            </GridCellRenderer>
        ));
    }
    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            isChoiceHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} showColumnHeaderId />
            )}
        </GridCellRenderer>
    );
}

function renderTextCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { question } = props;
    const cellVariable = column.variables.find(
        (variableItem) => variableItem.choiceList.length > 0,
    );
    const shouldSplitColumn = isSplitColumn(column);
    if (cellVariable && row.type === RowType.Text && shouldSplitColumn) {
        return cellVariable.choiceList.map((choice) => (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={choice.index}
                isColumnHeader
                validationResult={props.validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <Text {...props} column={column} row={row} index={choice.index} />
            </GridCellRenderer>
        ));
    }

    let cellVariableDefaultChoiceListLength = cellVariable?.choiceList?.length;

    if (!column.variables.every((e) => e.choiceList?.length === 0)) {
        cellVariableDefaultChoiceListLength = question.variables?.find(
            (v) => v.name === cellVariable.name,
        ).choiceList?.length;
    }

    const colSpan = shouldSplitColumn && cellVariable ? cellVariableDefaultChoiceListLength : 1;
    if (colSpan === 0) {
        return null;
    }

    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            colSpan={colSpan}
            isColumnHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} showColumnHeaderId />
            )}
        </GridCellRenderer>
    );
}

function MergedCells(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const { columns, validationResult, row, column } = props;
    let colSpan = getColumnCount(columns);
    if (!shouldShowRowLeftLabel(row)) {
        colSpan += 1;
    }
    if (!shouldShowRowRightLabel(row)) {
        colSpan += 1;
    }
    if (column.index > 0) {
        return null;
    }
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            colSpan={colSpan}
            isColumnHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} />
            )}
        </GridCellRenderer>
    );
}

function StandardRow(
    props: StyledProps & {
        row: AxisModel;
        showLeftLabels: boolean;
        showRightLabels: boolean;
        dataRowStripeIndex?: number;
    },
) {
    const {
        classes,
        columns,
        validationResult,
        row,
        question,
        questionnaire,
        isScreenSmall,
        showLeftLabels,
        showRightLabels,
        dataRowStripeIndex,
    } = props;
    const isStarRatting = question?.type === QuestionType.StarRatingGrid;
    const style = { display: isScreenSmall && !isStarRatting ? "block" : undefined };
    const isDataRow = !isHeaderRow(row);
    const theme = useTheme() as Theme;
    const isModernGridTable =
        isModernGridTableQuestion(question.type) && useModernGridTableStyles(theme);
    const useDuoChromeStriping = isModernGridDuoChromeStriping(theme);
    const isEvenDataRow =
        isDataRow && dataRowStripeIndex !== undefined && dataRowStripeIndex % 2 === 1;
    const isStripedDataRow = useDuoChromeStriping && isEvenDataRow;

    return (
        <ModernGridRowStripeContext.Provider
            value={isModernGridTable && isStripedDataRow}
        >
            <tr
                className={classnames(
                    classes.gridRow,
                    isHeaderRow(row)
                        ? "header-row"
                        : isModernGridTable
                          ? classnames(
                                classes.gridDataRowModern,
                                isStripedDataRow && classes.gridDataRowModernStriped,
                            )
                          : "data-row",
                )}
                role="row"
                style={style}
            >
            {showLeftLabels && <RowLeftLabel {...props} row={row} />}
            {columns.map((column) => {
                if (row.type === RowType.Variable) {
                    return (
                        <QuestionContext.Provider
                            key={`${row.index}_${column.index}`}
                            value={questionForCell(question, row, column, questionnaire)}
                        >
                            {renderVariableCells(props, row, column)}
                        </QuestionContext.Provider>
                    );
                }
                if (isRowMergeCells(row)) {
                    return (
                        <MergedCells
                            key={`${row.index}_${column.index}`}
                            {...props}
                            row={row}
                            column={column}
                        />
                    );
                }
                if (getUseChoiceTexts(row)) {
                    return renderChoiceTextCells(props, row, column);
                }
                return renderTextCells(props, row, column);
            })}
            {showRightLabels && <RowRightLabel {...props} row={row} />}
            </tr>
        </ModernGridRowStripeContext.Provider>
    );
}

function TotalCell(props: StyledProps & { column: AxisModel }) {
    const { column, question, validationResult, getColumnRunningTotal } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });
    const leftLabel = useTranslatable(column, "LeftLabel");
    const rightLabel = useTranslatable(column, "RightLabel");
    const ariaLabelledForTotalCell = `Q_${question.name}_HEADER_${column.index + 1}`;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            validationResult={validationResult}
            column={column}
        >
            <InputWrapper
                size={getSettingString(column, "Size", defaultSize(question))}
                leftLabel={leftLabel}
                rightLabel={rightLabel}
            >
                <output
                    className={classes.gridSummary}
                    aria-labelledby={ariaLabelledForTotalCell}
                    aria-live="off"
                >
                    {getColumnRunningTotal(column)}
                </output>
            </InputWrapper>
        </GridCellRenderer>
    );
}

function ColumnGroup(props: StyledProps) {
    const { columns } = props;
    const defaultWidth = defaultColumnsWidth(props);
    const firstWidth = firstColumnWidth(props) || defaultWidth;
    return (
        <colgroup>
            <col style={{ width: firstWidth }} />
            {times(getColumnCount(columns), (i) => (
                <col key={i} style={{ width: defaultWidth }} />
            ))}
        </colgroup>
    );
}

function TotalRow(props: StyledProps & { showLeftLabel: boolean }) {
    const { columns, question, validationResult, showLeftLabel } = props;
    return (
        <tr key="total" role="row">
            {showLeftLabel && (
                <GridCellRenderer
                    {...getRendererProps(props)}
                    isRowHeader
                    validationResult={validationResult}
                />
            )}
            {columns.map((column) => (
                <TotalCell key={column.index} {...props} column={column} />
            ))}
        </tr>
    );
}

function StandardRowTree(props: StyledProps) {
    const { rows, question, language } = props;
    const showLeftLabels = hasLeftLabels([...rows, question], language);
    const showRightLabels = hasRightLabels(rows, language);

    let dataRowStripeIndex = 0;
    const standardRows = rows.map((row) => {
        const isDataRow = !isHeaderRow(row);
        const rowStripeIndex = isDataRow ? dataRowStripeIndex++ : undefined;

        const standardRow = (
            <StandardRow
                key={row.index}
                {...props}
                row={row}
                showLeftLabels={showLeftLabels}
                showRightLabels={showRightLabels}
                dataRowStripeIndex={rowStripeIndex}
            />
        );

        return standardRow;
    });
    return (
        <>
            {standardRows}
            {question.type === QuestionType.RunningTotal && (
                <TotalRow key="total" {...props} showLeftLabel={showLeftLabels} />
            )}
        </>
    );
}

function TransposedHeaderRow(props: StyledProps) {
    const { rows } = props;
    return (
        <tr key="header" role="row">
            {rows.map((row) => (
                <GridCellRenderer
                    key={row.index}
                    {...getRendererProps(props)}
                    isChoiceHeader
                    validationResult={props.validationResult}
                    row={row}
                >
                    <LeftLabel {...props} axis={row} />
                    <RowOpenEnd row={row} {...props} gridRenderedRowIndex={row.index} />
                    {hasSetting(row, "OpenEndType") && (
                        <SignalListener
                            signal={props.validateSignal}
                            handler={() => props.validateRowOpenEnd(row)}
                        />
                    )}
                </GridCellRenderer>
            ))}
        </tr>
    );
}

function TransposedCells(props: StyledProps & { column: AxisModel; choice: ChoiceModel }) {
    const { column, choice, rows } = props;
    const dispatch = useDispatch();
    const choiceGridLeadCoords = useChoiceGridFirstAriaLeadCellCoords();
    const choiceGridMatrixRadioBridge = useChoiceGridMatrixRadioGroupBridge();

    if (!dispatch(evaluateIfChoiceVisible(choice))) {
        return null;
    }
    const cells = rows.map((row) => {
        const variable = variableForCell(row, column);
        if (row.type !== RowType.Variable) {
            return (
                <GridCellRenderer
                    {...getRendererProps(props)}
                    key={row.index}
                    isColumnHeader={false}
                    isRowHeader
                    validationResult={props.validationResult}
                    row={row}
                    column={column}
                    choice={choice}
                >
                    <ChoiceText {...props} choice={choice} row={row} />
                    <CellItems {...props} column={column} choice={choice} />
                    <SignalListener
                        signal={props.validateSignal}
                        handler={() => props.validateChoiceOpenEnd(column, choice)}
                    />
                </GridCellRenderer>
            );
        }
        const { validationResult, validateSignal, question, language } = props;
        const choiceGridGroupLabelIds =
            choiceGridMatrixRadioBridge != null
                ? undefined
                : getChoiceGridQuestionGroupLabelIdsForLeadCell(
                      question,
                      row,
                      column,
                      variable,
                      choice,
                      language,
                      choiceGridLeadCoords,
                  );
        const choiceControl = (
            <Choice row={row} column={column} variable={variable} choice={choice} {...props} />
        );
        return (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={row.index}
                validationResult={validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <SignalListener
                    signal={validateSignal}
                    handler={() => props.validateCell(row, column)}
                />
                {choiceGridMatrixRadioBridge != null &&
                column.type === ColumnType.RadioButton &&
                matchesChoiceGridAriaLeadChoiceCell(
                    row,
                    column,
                    variable,
                    choice,
                    choiceGridLeadCoords,
                ) ? (
                    <div
                        role="radiogroup"
                        aria-labelledby={choiceGridMatrixRadioBridge.questionLabelIds}
                        aria-owns={choiceGridMatrixRadioBridge.ownsIds}
                        tabIndex={-1}
                        className={props.classes.hybridRadioGroupBridge}
                    />
                ) : null}
                {choiceGridGroupLabelIds ? (
                    <div role="group" aria-labelledby={choiceGridGroupLabelIds}>
                        {choiceControl}
                    </div>
                ) : (
                    choiceControl
                )}
            </GridCellRenderer>
        );
    });

    return (
        <tr key={choice.index} role="row">
            {cells}
        </tr>
    );
}

function TransposedRowTree(props: StyledProps) {
    const { columns } = props;

    return (
        <>
            <TransposedHeaderRow {...props} />

            {columns.map((column) =>
                column.variables
                    .find((v) => v.choiceList?.length > 0)
                    ?.choiceList.map((choice) => (
                        <TransposedCells
                            key={choice.index}
                            {...props}
                            column={column}
                            choice={choice}
                        />
                    )),
            )}
        </>
    );
}

function onShouldDisableCell(
    props: GridRendererProps,
    row: AxisModel,
    column: AxisModel,
    variables: VariablesState,
    dispatch: Dispatch<any>,
) {
    const { columns } = props;
    const isCellDisabled = columns
        .filter((sequencedColumn) => sequencedColumn.variables[0])
        .some((sequencedColumn) => {
            const cellVariable = variableForCell(row, sequencedColumn);
            return cellVariable.choiceList.some((choice) => {
                const disabledColumns = getSettingString(choice, "DisableColumns");
                if (!isNullOrEmpty(disabledColumns)) {
                    const mentions = getMentions(variables, cellVariable.name);
                    if (mentions.some((m) => m.value === choice.value)) {
                        const isDisabled = disabledColumns
                            .replace(/\s/g, "")
                            .split(",")
                            .find((c) => c === column.index.toString());
                        if (isDisabled) {
                            const currentCell = variableForCell(row, column);
                            const currentMentions = getMentions(variables, currentCell.name);
                            if (currentMentions && currentMentions.length > 0) {
                                dispatch(setVariableMentions(currentCell.name, (m) => m.clear()));
                            }
                            return true;
                        }
                    }
                }
                return false;
            });
        });
    return isCellDisabled;
}

function isChoiceDisabled(
    props: StyledProps & {
        row: AxisModel;
        column: AxisModel;
        variable: VariableModel;
        choice: ChoiceModel;
    },
    mentions: MentionModel[],
    variables: VariablesState,
    dispatch: Dispatch<any>,
) {
    const { row, column, variable, choice } = props;
    if (onShouldDisableCell(props, row, column, variables, dispatch)) {
        return true;
    }

    const visibleChoiceMentions = mentions.filter((m) => isChoiceVisible(m.value, variable));
    const maxAllowedAnswers = parseInt(
        props.max || getSettingString(column, "MaxAllowedAnswers"),
        10,
    );
    return (
        !Number.isNaN(maxAllowedAnswers) &&
        !isChoiceSelected(choice, mentions) &&
        visibleChoiceMentions.length >= maxAllowedAnswers
    );
}

function isChoiceVisible(value: string, variable: VariableModel) {
    return variable.choiceList.some((choice) => choice.value === value);
}

function isChoiceSelected(choice: ChoiceModel, mentions: MentionModel[]) {
    return mentions.some((mention) => mention.value === choice.value);
}

function isCollapsibleExpanded(props: GridRendererProps, row: AxisModel) {
    return props.expandedRowsIndices && props.expandedRowsIndices.includes(row.index);
}

function CollapsibleContent(props: StyledProps & { row: AxisModel }) {
    const { validateSignal, shouldHideCell, columns, classes, language, row } = props;

    const visibleColumns = columns.filter(
        (column) => !shouldHideCell || !shouldHideCell(row, column),
    );
    const choices = visibleColumns.map((column) => {
        const variable = variableForCell(row, column);
        return variable.choiceList.map((choice) => (
            <div key={choice.index} className={classes.choiceList}>
                <Choice
                    row={row}
                    column={column}
                    variable={variable}
                    choice={choice}
                    label={translate(choice, "TEXT", language)}
                    isCollapsible
                    {...props}
                />
            </div>
        ));
    });

    const hasRightLabel = hasRightLabels([row], language);

    return (
        <Collapsible expanded={isCollapsibleExpanded(props, row)}>
            <SignalListener
                key="signal"
                handler={() => props.validateCell(row, visibleColumns[0])}
                signal={validateSignal}
            />
            {choices}
            {hasRightLabel && (
                <div className={classes.collapsibleGridFooter}>
                    <RightLabel {...props} axis={row} />
                </div>
            )}
        </Collapsible>
    );
}

function CollapsibleTitleRow(props: StyledProps & { row: AxisModel }) {
    const { classes, columns, row, question } = props;
    const theme = useTheme() as Theme;
    const isModernCollapsible =
        isModernGridTableQuestion(question.type) && useModernGridTableStyles(theme);

    return (
        <div className={classes.collapsibleGridContainer} key={row.index}>
            <div
                key={row.index}
                className={classnames(
                    classes.collapsibleGridSeparator,
                    isModernCollapsible && classes.collapsibleGridSeparatorModern,
                )}
            >
                <Title {...props} column={columns[0]} row={row} />
            </div>
        </div>
    );
}

function CollapsibleVariableRow(props: StyledProps & { row: AxisModel }) {
    const { classes, row, expandRow, addAccordionRef, question } = props;
    const theme = useTheme() as Theme;
    const isModernCollapsible =
        isModernGridTableQuestion(question.type) && useModernGridTableStyles(theme);
    const isExpanded = isCollapsibleExpanded(props, row);
    const className = classnames(
        classes.collapsibleGridHeader,
        isModernCollapsible && classes.collapsibleGridHeaderModern,
        !isModernCollapsible && !isExpanded && classes.collapsibleGridHeaderCollapse,
    );
    return (
        <div className={classes.collapsibleGridContainer} key={row.index}>
            <div
                className={className}
                ref={(ref) => addAccordionRef && addAccordionRef(row.index, ref)}
                onClick={() => expandRow(row)}
            >
                <LeftLabel {...props} axis={row} />
                {isModernCollapsible ? (
                    isExpanded ? (
                        <ModernSelectChevronUpIcon className={classes.collapsibleGridArrowModern} />
                    ) : (
                        <ModernSelectChevronDownIcon
                            className={classes.collapsibleGridArrowModern}
                        />
                    )
                ) : (
                    <FontAwesomeIcon icon="caret-up" className={classes.collapsibleGridArrow} />
                )}
            </div>
            <CollapsibleContent {...props} row={row} />
        </div>
    );
}

function CollapsibleRow(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    switch (row.type as RowType) {
        case RowType.Title:
            return <CollapsibleTitleRow {...props} row={row} />;
        case RowType.Variable:
            return <CollapsibleVariableRow {...props} row={row} />;
        case RowType.Text:
        default:
            return null;
    }
}

/** Avoid "?." when joining title + body with ". " for aria-label. */
function stripTrailingTerminalPunctuationForAriaJoin(s: string): string {
    const trimmed = s.trimEnd();
    const without = trimmed.replace(/[.?!:]+\s*$/, "").trimEnd();
    return without.length > 0 ? without : trimmed;
}

function StandardGrid(props: ReferencedElementProps) {
    const { question, classes, elementRef } = props;
    const theme = useTheme() as Theme;
    const includeHybridGridSubHeaderAriaLabelling =
        question.type === QuestionType.HybridGrid &&
        props.rows.some((r) => r.type === RowType.Text);
    const firstRunningTotalVariableCell = React.useMemo(() => {
        if (question.type !== QuestionType.RunningTotal) {
            return null;
        }
        return findFirstRunningTotalVariableCell(props.rows, props.columns, props.shouldHideCell);
    }, [question.type, props.rows, props.columns, props.shouldHideCell]);

    const choiceGridAriaLeadCell = React.useMemo(
        () => getChoiceGridAriaLeadCellCoords(props),
        [question.type, props.rows, props.columns, props.shouldHideCell, props.question],
    );

    const choiceGridMatrixRadioBridge = React.useMemo(
        () => getChoiceGridMatrixRadioGroupBridgeValue(props),
        [
            question.type,
            props.language,
            props.rows,
            props.columns,
            props.shouldHideCell,
            props.question,
        ],
    );

    const hybridGridAriaLeadCell = React.useMemo((): HybridGridFirstAriaLeadCellCoords | null => {
        if (question.type !== QuestionType.HybridGrid) {
            return null;
        }
        return isTransposedGrid(props)
            ? findFirstHybridGridAriaLeadCellTransposed(
                  props.rows,
                  props.columns,
                  props.shouldHideCell,
              )
            : findFirstHybridGridAriaLeadCell(props.rows, props.columns, props.shouldHideCell);
    }, [question.type, props.rows, props.columns, props.shouldHideCell, props.question]);

    const rows = isTransposedGrid(props) ? (
        <TransposedRowTree {...props} />
    ) : (
        <StandardRowTree {...props} />
    );
    const isChoiceGridTable =
        question.type === QuestionType.ChoiceGrid ||
        question.type === QuestionType.ImageChoiceGrid;
    const isModernGridTable =
        isModernGridTableQuestion(question.type) && useModernGridTableStyles(theme);
    const isStarRatingGrid = question.type === QuestionType.StarRatingGrid;
    const textRaw = translate(question, "TEXT", props.language);
    const shortRaw = translate(question, "SHORT_TEXT", props.language);

    // Use aria-label (stripped plain text) as the table accessible name for star rating grids.
    // We intentionally avoid aria-describedby here to prevent duplicate announcements in table navigation.
    let starRatingAriaLabel: string | undefined;
    if (isStarRatingGrid) {
        const titlePlain = stripHtmlForAccessibleName(translate(question, "TITLE", props.language));
        const textPlain = stripHtmlForAccessibleName(textRaw);
        const shortPlain = stripHtmlForAccessibleName(shortRaw);
        const bodyPlain = textPlain || shortPlain;
        const parts = [titlePlain, bodyPlain].filter(Boolean);
        if (parts.length === 0) {
            starRatingAriaLabel = undefined;
        } else if (parts.length === 1) {
            starRatingAriaLabel = parts[0];
        } else {
            const titleForJoin = stripTrailingTerminalPunctuationForAriaJoin(titlePlain);
            starRatingAriaLabel = `${titleForJoin}. ${bodyPlain}`;
        }
    }

    const shouldUsePresentationRole =
        question.type === QuestionType.RunningTotal ||
        question.type === QuestionType.ChoiceGrid ||
        question.type === QuestionType.ImageChoiceGrid ||
        question.type === QuestionType.HybridGrid;
    const table = (
        <table
            ref={elementRef as React.Ref<HTMLTableElement>}
            // These grids use explicit aria-labelledby on each interactive control
            // for row/column/question context, so suppress table-structure announcements.
            role={shouldUsePresentationRole ? "presentation" : undefined}
            className={classnames(
                classes.grid,
                isChoiceGridTable && classes.choiceGrid,
                isModernGridTable && classes.gridTableModern,
                isStarRatingGrid && classes.gridViewStarRating,
                "grid-view",
            )}
            aria-label={starRatingAriaLabel}
        >
            <ColumnGroup {...props} />
            <tbody
                className={classnames(
                    isChoiceGridTable && !isModernGridTable && classes.gridBody,
                    question.type === QuestionType.ImageChoiceGrid &&
                        !isModernGridTable &&
                        classes.gridBodyImageChoice,
                )}
            >
                {rows}
            </tbody>
        </table>
    );

    const wrapHybridSubHeaderAria = (node: React.ReactNode) => (
        <HybridGridSubHeaderAriaContext.Provider value={includeHybridGridSubHeaderAriaLabelling}>
            {node}
        </HybridGridSubHeaderAriaContext.Provider>
    );

    if (question.type === QuestionType.RunningTotal) {
        return wrapHybridSubHeaderAria(
            <RunningTotalFirstVariableCellContext.Provider value={firstRunningTotalVariableCell}>
                {table}
            </RunningTotalFirstVariableCellContext.Provider>,
        );
    }
    const isAriaLeadChoiceGridType =
        question.type === QuestionType.ChoiceGrid || question.type === QuestionType.ImageChoiceGrid;
    if (isAriaLeadChoiceGridType) {
        return wrapHybridSubHeaderAria(
            <ChoiceGridFirstAriaLeadCellContext.Provider value={choiceGridAriaLeadCell}>
                <ChoiceGridMatrixRadioGroupBridgeContext.Provider value={choiceGridMatrixRadioBridge}>
                    {table}
                </ChoiceGridMatrixRadioGroupBridgeContext.Provider>
            </ChoiceGridFirstAriaLeadCellContext.Provider>,
        );
    }
    if (question.type === QuestionType.HybridGrid) {
        return wrapHybridSubHeaderAria(
            <HybridGridFirstAriaLeadCellContext.Provider value={hybridGridAriaLeadCell}>
                {table}
            </HybridGridFirstAriaLeadCellContext.Provider>,
        );
    }
    return wrapHybridSubHeaderAria(table);
}

function CollapsibleGrid(props: ReferencedElementProps) {
    const { classes, elementRef, rows, question } = props;
    const theme = useTheme() as Theme;
    const isModernCollapsible =
        isModernGridTableQuestion(question.type) && useModernGridTableStyles(theme);

    return (
        <div
            ref={elementRef as React.Ref<HTMLDivElement>}
            className={classnames(
                classes.collapsibleGrid,
                isModernCollapsible && classes.collapsibleGridModern,
            )}
        >
            {rows.map((row) => (
                <CollapsibleRow key={row.index} {...props} row={row} />
            ))}
        </div>
    );
}

function ResponsiveChoiceGrid(props: StyledProps) {
    const elementRef: React.Ref<HTMLElement> = React.useRef(null);

    const [testFitElementInitialWidth, setTestFitElementInitialWidth] = React.useState<number>();
    const [shouldCollapse, setShouldCollapse] = React.useState<boolean>();

    const { question, rows, columns } = props;
    const isImageChoiceGrid = question.type === QuestionType.ImageChoiceGrid;

    const onWindowSizeChanged = React.useCallback(() => {
        const wrapperShouldCollapse = (wrapper: Element) => {
            const { width } = wrapper.getBoundingClientRect();
            return width < SMALL_WIDTH || width < testFitElementInitialWidth;
        };

        const wrapper = elementRef.current.closest(".page__wrapper--dynamic");
        setShouldCollapse(wrapper && (isImageChoiceGrid || wrapperShouldCollapse(wrapper)));
    }, [testFitElementInitialWidth, isImageChoiceGrid]);

    React.useEffect(() => {
        if (!elementRef.current) {
            return;
        }
        const { width } = elementRef.current.getBoundingClientRect();
        if (!shouldCollapse && testFitElementInitialWidth !== width) {
            setTestFitElementInitialWidth(width);
        }
    }, [testFitElementInitialWidth, rows, columns, shouldCollapse]);

    React.useLayoutEffect(() => {
        window.addEventListener("resize", onWindowSizeChanged);
        onWindowSizeChanged();
        return function cleanup() {
            window.removeEventListener("resize", onWindowSizeChanged);
        };
    }, [onWindowSizeChanged]);

    const choiceGridAriaLeadCell = React.useMemo(
        () => getChoiceGridAriaLeadCellCoords(props),
        [props.question, props.question.type, props.rows, props.columns, props.shouldHideCell],
    );
    const choiceGridMatrixRadioBridge = React.useMemo(
        () => getChoiceGridMatrixRadioGroupBridgeValue(props),
        [
            props.question.type,
            props.language,
            props.rows,
            props.columns,
            props.shouldHideCell,
            props.question,
        ],
    );
    const isChoiceGridForProvider =
        question.type === QuestionType.ChoiceGrid || question.type === QuestionType.ImageChoiceGrid;

    const gridContent = shouldCollapse ? (
        <CollapsibleGrid {...props} elementRef={elementRef} />
    ) : (
        <StandardGrid {...props} elementRef={elementRef} />
    );

    return isChoiceGridForProvider ? (
        <ChoiceGridFirstAriaLeadCellContext.Provider value={choiceGridAriaLeadCell}>
            <ChoiceGridMatrixRadioGroupBridgeContext.Provider value={choiceGridMatrixRadioBridge}>
                {gridContent}
            </ChoiceGridMatrixRadioGroupBridgeContext.Provider>
        </ChoiceGridFirstAriaLeadCellContext.Provider>
    ) : (
        gridContent
    );
}

function ResponsiveStarRatingGrid(props: StyledProps) {
    const elementRef: React.Ref<HTMLElement> = React.useRef(null);
    const [isScreenSmall, setIsScreenSmall] = React.useState<boolean>();

    const onWindowSizeChanged = React.useCallback(() => {
        const wrapper = elementRef.current.closest(".page__wrapper--dynamic");
        setIsScreenSmall(wrapper !== null);
    }, [elementRef]);

    React.useLayoutEffect(() => {
        window.addEventListener("resize", onWindowSizeChanged);
        onWindowSizeChanged();
        return function cleanup() {
            window.removeEventListener("resize", onWindowSizeChanged);
        };
    }, [onWindowSizeChanged]);

    return <StandardGrid {...props} isScreenSmall={isScreenSmall} elementRef={elementRef} />;
}

function ResetButton(props: GridRendererProps) {
    const { onReset, theme } = props;

    const question = useQuestion();
    const defaultClearButtonText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const clearButtonText = useTranslatable(question, "ResetButtonText") || defaultClearButtonText;

    return <Button size="full" text={useRecall(clearButtonText)} onClick={() => onReset()} />;
}

function Grid(props: StyledProps) {
    const { question, rows, columns } = props;
    if (question.type === QuestionType.StarRatingGrid) {
        return <ResponsiveStarRatingGrid {...props} />;
    }
    const isChoiceGrid = [QuestionType.ChoiceGrid, QuestionType.ImageChoiceGrid].includes(
        question.type,
    );

    const hasColumnOpenEnd =
        columns &&
        columns.length &&
        columns[0].variables.some((variable) => variable.choiceList.some(hasOpenEnd));
    const hasRowOpenEnd = rows.some(hasOpenEnd);
    const isResponsiveChoiceGrid = isChoiceGrid && !(hasRowOpenEnd || hasColumnOpenEnd);

    return isResponsiveChoiceGrid ? (
        <ResponsiveChoiceGrid {...props} />
    ) : (
        <StandardGrid {...props} />
    );
}

export default React.memo(function GridRenderer(props: GridRendererProps) {
    const { question, validationResult: localValidationResult } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });
    const reduxErrorsByVariableName = useSelector(
        (state: StoreState) => state.interview.validation?.errorsByVariableName,
    );
    const validationResult = React.useMemo(
        () =>
            question.type === QuestionType.RunningTotal
                ? mergeRunningTotalValidationResult(
                      question,
                      localValidationResult,
                      reduxErrorsByVariableName,
                  )
                : localValidationResult,
        [question, localValidationResult, reduxErrorsByVariableName],
    );

    const hasResetButton =
        question.type === QuestionType.RunningTotal && shouldDisplayResetButton(props);

    return (
        <>
            <Grid {...props} validationResult={validationResult} classes={classes} />
            {hasResetButton && <ResetButton {...props} />}
        </>
    );
});
